/**
 * Sends usage events (shared/usage.ts) to Taretto's own server, and only while the person has said yes there. Each
 * event goes as it happens, on its own: nothing is kept to send later, so turning sharing off stops it at once. The
 * server checks the answer again before it writes anything (server/routes/usage.ts).
 */
import { useSyncExternalStore } from 'react';
import type { UsageBrowser, UsageDevice, UsageEventName, UsageFields } from '@shared/usage';

let sharing = false;
const listeners = new Set<() => void>();

/** Set from GET /api/usage (UsageSync in App.tsx), and to false the moment the person turns it off. */
export function setUsageSharing(on: boolean): void {
  if (sharing === on) return;
  sharing = on;
  for (const listener of listeners) listener();
}

export function isSharingUsage(): boolean {
  return sharing;
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Whether this device sends usage events, for what is counted as it's shown (the page the app opens on). */
export function useSharingUsage(): boolean {
  return useSyncExternalStore(subscribe, isSharingUsage, () => false);
}

/** Sends one event, or does nothing while sharing is off. Never throws, and never waits. */
export function track<N extends UsageEventName>(name: N, fields: UsageFields<N>): void {
  if (!sharing) return;
  try {
    void fetch('/api/usage/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, fields }),
      // Still sent while the page closes (a brew left without saving).
      keepalive: true,
    }).catch(() => {});
  } catch {
    // No fetch (tests), or the browser refused: the event is just not counted.
  }
}

/** The browser, from its user agent. Bluefy and the Chromium browsers first: they all say Safari or Chrome too. */
export function browserOf(ua: string): UsageBrowser {
  if (/\bBluefy\//.test(ua)) return 'bluefy';
  if (/\bEdg(e|A|iOS)?\//.test(ua)) return 'edge';
  if (/\bSamsungBrowser\//.test(ua)) return 'samsung';
  if (/\bOPR\/|\bOpera\b/.test(ua)) return 'opera';
  if (/\bFirefox\/|\bFxiOS\//.test(ua)) return 'firefox';
  if (/\bChrome\/|\bCriOS\//.test(ua)) return 'chrome';
  if (/\bSafari\//.test(ua)) return 'safari';
  return 'other';
}

/** The kind of device. iPadOS asks for desktop sites as a Mac, which is told apart by its touch screen. */
export function deviceOf(ua: string, touchPoints: number): UsageDevice {
  if (/\biPad\b/.test(ua) || (/\bMacintosh\b/.test(ua) && touchPoints > 1)) return 'ipad';
  if (/\biPhone\b|\biPod\b/.test(ua)) return 'iphone';
  if (/\bAndroid\b/.test(ua)) return 'android';
  if (/\bCrOS\b/.test(ua)) return 'chromeos';
  if (/\bMacintosh\b/.test(ua)) return 'mac';
  if (/\bWindows\b/.test(ua)) return 'windows';
  if (/\bLinux\b/.test(ua)) return 'linux';
  return 'other';
}

/** This browser and device, for the scale events. */
export function thisBrowser(): { browser: UsageBrowser; device: UsageDevice } {
  const ua = typeof navigator === 'undefined' ? '' : (navigator.userAgent ?? '');
  const touch = typeof navigator === 'undefined' ? 0 : (navigator.maxTouchPoints ?? 0);
  return { browser: browserOf(ua), device: deviceOf(ua, touch) };
}
